import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { Channel, ChannelInput, ChannelQrResponse } from '@/types/channels'
import type { PrivacyRequestDetail } from '@/types/privacy'

export const channelsKeys = {
  list: (orgId: string | null) => ['channels', 'list', orgId] as const,
  detail: (orgId: string | null, channelId: string | null) =>
    ['channels', 'detail', orgId, channelId] as const,
}

export function useChannels(orgId: string | null, enabled = true) {
  return useQuery<Channel[]>({
    queryKey: channelsKeys.list(orgId),
    queryFn: async () => {
      const res = await api.get<Channel[]>(`/orgs/${orgId}/channels`)
      return res.data
    },
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

export function useCreateChannel(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: ChannelInput) => {
      const res = await api.post<Channel>(`/orgs/${orgId}/channels`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: channelsKeys.list(orgId) })
    },
  })
}

export function useUpdateChannel(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      channelId,
      data,
    }: {
      channelId: string
      data: Partial<ChannelInput>
    }) => {
      const res = await api.put<Channel>(
        `/orgs/${orgId}/channels/${channelId}`,
        data
      )
      return res.data
    },
    onSuccess: (channel) => {
      void qc.invalidateQueries({ queryKey: channelsKeys.list(orgId) })
      void qc.invalidateQueries({
        queryKey: channelsKeys.detail(orgId, channel.id),
      })
    },
  })
}

/**
 * Arquivar um canal abre uma operação de offboarding no servidor (para
 * automações, desinscreve/derruba no provedor, remove credenciais). A resposta
 * traz a operação com status e pendências.
 */
export function useDeleteChannel(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channelId: string) => {
      const res = await api.delete<{
        success: boolean
        archived: boolean
        operation: PrivacyRequestDetail
      }>(`/orgs/${orgId}/channels/${channelId}`)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: channelsKeys.list(orgId) })
      void qc.invalidateQueries({ queryKey: ['privacy-requests', orgId] })
    },
  })
}

export function useConnectChannel(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channelId: string) => {
      const res = await api.post<ChannelQrResponse>(
        `/orgs/${orgId}/channels/${channelId}/connect`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: channelsKeys.list(orgId) })
    },
  })
}

export function useDisconnectChannel(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channelId: string) => {
      const res = await api.post<ChannelQrResponse>(
        `/orgs/${orgId}/channels/${channelId}/disconnect`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: channelsKeys.list(orgId) })
    },
  })
}

export async function fetchChannelQr(
  orgId: string,
  channelId: string
): Promise<ChannelQrResponse> {
  const res = await api.get<ChannelQrResponse>(
    `/orgs/${orgId}/channels/${channelId}/qr`
  )
  return res.data
}

export interface MetaWebhookInfo {
  callbackUrl: string
  verifyToken: string
  subscribeField: string
}

export function useMetaWebhookInfo(
  orgId: string | null,
  channelId: string | null,
  enabled = true
) {
  return useQuery<MetaWebhookInfo>({
    queryKey: ['channels', 'meta-webhook', orgId, channelId],
    queryFn: async () => {
      const res = await api.get<MetaWebhookInfo>(
        `/orgs/${orgId}/channels/${channelId}/meta/webhook`
      )
      return res.data
    },
    enabled: !!orgId && !!channelId && enabled,
    staleTime: 5 * 60_000,
  })
}
